/* eslint-disable no-unused-vars */
import React, {useState, useEffect, useCallback, useMemo} from 'react'
import {useIntl} from 'react-intl'
import {PageTitle} from '../../../core'

import * as Yup from 'yup'
import {useFormik} from 'formik'
import 'flatpickr/dist/flatpickr.css'

import {
  KTCard,
  KTCardBody,
  Select,
  ChartDonut,
  ChartColumn,
  ChartLine,
  Loading,
} from '../../../components'

import {useDispatch, useSelector} from 'react-redux'
import {projectLookup} from '../../../redux/slices/lookup.slice'
import {
  workPlanProjectCompleteRateByProjectGetAll,
  workPlanConstructionSiteCompleteRateByProjectGetAll,
  workPlanBlockCompleteRateByConstructionSiteGetAll,
  workPlanRootTaskCompleteRateByProjectGetAll,
  workPlanDailyTaskCompleteRateByProjectGetAll,
} from '../../../redux/slices/reporting.slice'

import {
  useMemoizedValidationSchema,
  useMemoizedFormValues
} from '../../../utils/performanceOptimizations'

const selectLookupData = (state) => ({
  projectLookupList: state.lookup.projectLookupList
})

const selectReportingData = (state) => ({
  loading: state.reporting.loading,
  projectCompleteRateByProject: state.reporting.projectCompleteRateByProject,
  constructionSiteCompleteRateByProject: state.reporting.constructionSiteCompleteRateByProject,
  blockCompleteRateWithConstructionSiteByProject: state.reporting.blockCompleteRateWithConstructionSiteByProject,
  rootTaskCompleteRateByProject: state.reporting.rootTaskCompleteRateByProject,
  dailyTaskCompleteRateByProject: state.reporting.dailyTaskCompleteRateByProject,
})

function Report() {
  const {projectLookupList} = useSelector(selectLookupData)
  const {
    loading,
    projectCompleteRateByProject,
    constructionSiteCompleteRateByProject,
    blockCompleteRateWithConstructionSiteByProject,
    rootTaskCompleteRateByProject,
    dailyTaskCompleteRateByProject,
  } = useSelector(selectReportingData)

  const intl = useIntl()
  const dispatch = useDispatch()

  const [dailyNewData, setDailyNewData] = useState([])
  const [rootNewData, setRootNewData] = useState([])

  const validSchema = useMemoizedValidationSchema(() => Yup.object().shape({
    ProjectId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
  }), [intl])

  const formValues = useMemoizedFormValues({
    ProjectId: '',
  })

  const handleSubmit = useCallback(async (values, {setSubmitting}) => {
    setSubmitting(true)
    dispatch(workPlanProjectCompleteRateByProjectGetAll(values))
    dispatch(workPlanConstructionSiteCompleteRateByProjectGetAll(values))
    dispatch(workPlanBlockCompleteRateByConstructionSiteGetAll(values))
    dispatch(workPlanRootTaskCompleteRateByProjectGetAll(values))
    dispatch(workPlanDailyTaskCompleteRateByProjectGetAll(values))
  }, [dispatch])

  const formik = useFormik({
    initialValues: formValues,
    validationSchema: validSchema,
    onSubmit: handleSubmit,
  })

  useEffect(() => {
    dispatch(projectLookup())
  }, [dispatch])

  useEffect(() => {
    if (dailyTaskCompleteRateByProject) {
      const newData = []

      for (const key in dailyTaskCompleteRateByProject[0]) {
        if (key !== 'title') {
          const data = dailyTaskCompleteRateByProject.map((item) => item && item[key])
          newData.push({
            data,
            name: key.toUpperCase(),
          })
        }
      }

      setDailyNewData(newData)
    }
  }, [dailyTaskCompleteRateByProject])

  useEffect(() => {
    if (rootTaskCompleteRateByProject) {
      const newData = []

      for (const key in rootTaskCompleteRateByProject[0]) {
        if (key !== 'name') {
          const data = rootTaskCompleteRateByProject.map((item) => item && item[key])
          newData.push({
            data,
            name: key.toUpperCase(),
          })
        }
      }

      setRootNewData(newData)
    }
  }, [rootTaskCompleteRateByProject])

  const projectChartData = useMemo(() => {
    if (!projectCompleteRateByProject || !projectCompleteRateByProject.length) return []
    
    return [
      projectCompleteRateByProject[0] && projectCompleteRateByProject[0].remainingWork,
      projectCompleteRateByProject[0] && projectCompleteRateByProject[0].completedWork,
    ]
  }, [projectCompleteRateByProject])

  const constructionSiteChartData = useMemo(() => {
    if (!constructionSiteCompleteRateByProject) return []
    
    return [
      {
        name: intl.formatMessage({
          id: 'REPORT.WORKPROGRAM.CONSTRUCTIONSITECOMPLETERATEBYPROJECT.COMPLETEDWORK',
        }),
        data: constructionSiteCompleteRateByProject.map((item) => item && item.completedWork),
      },
      {
        name: intl.formatMessage({
          id: 'REPORT.WORKPROGRAM.CONSTRUCTIONSITECOMPLETERATEBYPROJECT.REMAININGWORK',
        }),
        data: constructionSiteCompleteRateByProject.map((item) => item && item.remainingWork),
      },
    ]
  }, [constructionSiteCompleteRateByProject, intl])

  const constructionSiteCategories = useMemo(() => {
    if (!constructionSiteCompleteRateByProject) return []
    
    return constructionSiteCompleteRateByProject.map(
      (item) => item && item.constructionSiteName
    )
  }, [constructionSiteCompleteRateByProject])

  const rootTaskCategories = useMemo(() => {
    if (!rootTaskCompleteRateByProject) return []
    
    return rootTaskCompleteRateByProject.map((item) => item && item.name)
  }, [rootTaskCompleteRateByProject])

  const dailyTaskCategories = useMemo(() => {
    if (!dailyTaskCompleteRateByProject) return []
    
    return dailyTaskCompleteRateByProject.map((item) => item && item.title)
  }, [dailyTaskCompleteRateByProject])

  const blockChartData = useMemo(() => {
    if (!blockCompleteRateWithConstructionSiteByProject) return []
    
    return blockCompleteRateWithConstructionSiteByProject.map((item) => [
      {
        name: intl.formatMessage({
          id: 'REPORT.WORKPROGRAM.CONSTRUCTIONSITECOMPLETERATEBYPROJECT.COMPLETEDWORK',
        }),
        data: item && item.blockList && item.blockList.map((blockItem) => blockItem && blockItem.completedWork),
      },
      {
        name: intl.formatMessage({
          id: 'REPORT.WORKPROGRAM.CONSTRUCTIONSITECOMPLETERATEBYPROJECT.REMAININGWORK',
        }),
        data: item && item.blockList && item.blockList.map((blockItem) => blockItem && blockItem.remainingWork),
      },
    ])
  }, [blockCompleteRateWithConstructionSiteByProject, intl])

  const blockCategories = useMemo(() => {
    if (!blockCompleteRateWithConstructionSiteByProject) return []
    
    return blockCompleteRateWithConstructionSiteByProject.map((item) => 
      item && item.blockList && item.blockList.map((blockItem) => blockItem && blockItem.blockName)
    )
  }, [blockCompleteRateWithConstructionSiteByProject])

  return (
    <>
      <PageTitle>{intl.formatMessage({id: 'MENU.REPORTING.WORKPROGRAM'})}</PageTitle>

      <div className='row gy-10 gx-xl-10'>
        <div className='col-xl-12'>
          <KTCard className='mb-5'>
            <KTCardBody className='p-9'>
              <form className='form' onSubmit={formik.handleSubmit} noValidate>
                <div className='row mb-6'>
                  <div className='col-lg-6'>
                    <div className='row mb-6'>
                      <label className='col-lg-12 fw-bold fs-6 mb-2'>
                        {intl.formatMessage({id: 'REPORT.WORKPROGRAM.PROJECT'})}
                      </label>
                      <div className='col-lg-12 fv-row fv-plugins-icon-container'>
                        <Select
                          data={projectLookupList}
                          required
                          {...formik.getFieldProps('ProjectId')}
                          onChange={(item) =>
                            formik.setFieldValue('ProjectId', Number(item && item.value))
                          }
                        />
                        {formik.touched.ProjectId && formik.errors.ProjectId && (
                          <div className='fv-plugins-message-container'>
                            <div className='fv-help-block'>
                              <span role='alert'>{formik.errors.ProjectId}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className='row pt-5'>
                  <div className='col-lg-12 fv-row fv-plugins-icon-container d-flex gap-3 justify-content-center'>
                    <button
                      type='submit'
                      className='btn btn-primary'
                      data-kt-users-modal-action='submit'
                      disabled={formik.isSubmitting || !formik.isValid || !formik.touched}
                    >
                      <span className='indicator-label'>
                        {intl.formatMessage({id: 'BUTTON.SEARCH'})}
                      </span>
                      {formik.isSubmitting && (
                        <span className='indicator-progress'>
                          {intl.formatMessage({id: 'PLEASE'})}
                          <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                        </span>
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </KTCardBody>
          </KTCard>
        </div>
      </div>

      <div className='row gy-5 g-xl-10'>
        {projectCompleteRateByProject && projectCompleteRateByProject.length > 0 && (
          <div className='col-xl-6'>
            <ChartDonut
              chartHeight='418px'
              categories={[
                intl.formatMessage({
                  id: 'REPORT.WORKPROGRAM.PROJECTCOMPLETERATEBYPROJECT.REMAININGWORK',
                }),
                intl.formatMessage({
                  id: 'REPORT.WORKPROGRAM.PROJECTCOMPLETERATEBYPROJECT.COMPLETEDWORK',
                }),
              ]}
              data={projectChartData}
              title={intl.formatMessage({id: 'REPORT.WORKPROGRAM.PROJECTCOMPLETERATEBYPROJECT'})}
            />
          </div>
        )}

        {constructionSiteCompleteRateByProject &&
          constructionSiteCompleteRateByProject.length > 0 && (
            <div className='col-xl-6'>
              <ChartColumn
                chartHeight='400px'
                categories={constructionSiteCategories}
                data={constructionSiteChartData}
                isMultipleColumn={true}
                title={intl.formatMessage({
                  id: 'REPORT.WORKPROGRAM.CONSTRUCTIONSITECOMPLETERATEBYPROJECT',
                })}
                stacked
                dataLabels
                barColors={['#01e396', '#018ffb']}
              />
            </div>
          )}
        {rootNewData && rootNewData.length > 0 && (
          <div className='col-xl-6'>
            <ChartColumn
              chartHeight='400px'
              categories={rootTaskCategories}
              data={rootNewData}
              isMultipleColumn={true}
              horizontal={true}
              title={intl.formatMessage({id: 'REPORT.WORKPROGRAM.ROOTCOMPLETERATEBYPROJECT'})}
            />
          </div>
        )}

        {dailyNewData && dailyNewData.length > 0 && (
          <div className='col-xl-6'>
            <ChartLine
              chartHeight='400px'
              categories={dailyTaskCategories}
              data={dailyNewData}
              isMultipleColumn={true}
              title={intl.formatMessage({id: 'REPORT.WORKPROGRAM.DAILYCOMPLETERATEBYPROJECT'})}
              barColors={['#01e396', '#018ffb']}
            />
          </div>
        )}

        {blockCompleteRateWithConstructionSiteByProject && blockCompleteRateWithConstructionSiteByProject.map((item, index) => {
          return (
            <div className={item && item.blockList && item.blockList.length > 10 ? 'col-xl-12' : 'col-xl-6'} key={index}>
              <ChartColumn
                chartHeight='400px'
                categories={blockCategories[index] || []}
                data={blockChartData[index] || []}
                isMultipleColumn={true}
                title={item && item.constructionSiteName}
                stacked
                dataLabels
                barColors={['#01e396', '#018ffb']}
              />
            </div>
          )
        })}
      </div>

      {loading && <Loading />}
    </>
  )
}
export {Report}
